import AdGroups from './AdGroups'
import Ads from './AdRequests'
import LensMetrics from './Metrics'
import TimeSeries from './TimeSeriesRequests'
import Lens from './LensRequests'
import LensReport from './ReportRequests'
import LensPreset from './PresetRequests'
import LensFolder from './FolderRequests'
import Tags from './TagsRequests'
import LensDemoAPI from './demo/LensDemoAPI'
import { isDemoId, isDemoPresetId } from '@/utils/lens/demo/lensDemoConstants'

// Calls that target the demo Lens (or one of its reports / shares) are served
// by LensDemoAPI instead of the server. `pickId` names the argument carrying
// the id for each method, so routing never guesses from payload contents.
const routeDemo = (realFn, demoFn, pickId) => (...args) => {
  return isDemoId(pickId(...args)) ? demoFn(...args) : realFn(...args)
}

// Writes with no demo stand-in (rename, delete, duplicate, sharing) never
// reach the server for demo ids
const DEMO_READ_ONLY_MESSAGE = "This isn't available on the demo Lens"
const blockDemo = (realFn, pickId) => routeDemo(realFn, () => Promise.reject(new Error(DEMO_READ_ONLY_MESSAGE)), pickId)

const routeDemoPreset = (realFn, demoFn) => (presetId, ...args) => {
  return isDemoPresetId(presetId) ? demoFn(presetId, ...args) : realFn(presetId, ...args)
}

const firstLensId = (items) => (Array.isArray(items) ? items[0] : items)?.lens_id

export default {
  Lens: {
    ...Lens,
    get: routeDemo(Lens.get, LensDemoAPI.Lens.get, (id) => id),
    getInsightByLens: routeDemo(Lens.getInsightByLens, LensDemoAPI.Lens.getInsightByLens, (lensId) => lensId),
    getAdComments: routeDemo(Lens.getAdComments, LensDemoAPI.Lens.getAdComments, (lensId) => lensId),
    // Nothing to refresh on demo data
    refreshLensData: routeDemo(Lens.refreshLensData, async () => ({ data: {} }), (lensId) => lensId),
    update: blockDemo(Lens.update, (id) => id),
    deleteLens: blockDemo(Lens.deleteLens, (id) => id)
  },
  LensReport: {
    ...LensReport,
    getByID: routeDemo(LensReport.getByID, LensDemoAPI.LensReport.getByID, (reportId) => reportId),
    getAll: routeDemo(LensReport.getAll, LensDemoAPI.LensReport.getAll, (query) => query?.lens_id),
    create: routeDemo(LensReport.create, LensDemoAPI.LensReport.create, (payload) => payload?.lens_id),
    update: routeDemo(LensReport.update, LensDemoAPI.LensReport.update, (reportId) => reportId),
    remove: blockDemo(LensReport.remove, (reportId) => reportId),
    duplicate: blockDemo(LensReport.duplicate, (reportId) => reportId),
    // Sharing is disabled on the demo Lens
    createShare: blockDemo(LensReport.createShare, (payload) => payload?.lens_id || payload?.report_id),
    getAllShares: blockDemo(LensReport.getAllShares, (reportId) => reportId)
  },
  LensPreset: {
    ...LensPreset,
    getAll: routeDemo(LensPreset.getAll, LensDemoAPI.LensPreset.getAll, (lensId) => lensId),
    create: routeDemo(LensPreset.create, LensDemoAPI.LensPreset.create, (payload, lensId) => lensId),
    update: routeDemoPreset(LensPreset.update, LensDemoAPI.LensPreset.update),
    remove: routeDemoPreset(LensPreset.remove, LensDemoAPI.LensPreset.remove)
  },
  AdGroups: {
    ...AdGroups,
    getLensGroups: routeDemo(AdGroups.getLensGroups, LensDemoAPI.AdGroups.getLensGroups, (lensId) => lensId),
    getLensGroupAds: routeDemo(AdGroups.getLensGroupAds, LensDemoAPI.AdGroups.getLensGroupAds, (lensId) => lensId),
    getLensCreativeGroup: routeDemo(AdGroups.getLensCreativeGroup, LensDemoAPI.AdGroups.getLensCreativeGroup, (idPayload) => idPayload?.lensId),
    createShareLink: blockDemo(AdGroups.createShareLink, (creativePayload) => creativePayload?.lensId)
  },
  Ads: {
    ...Ads,
    getAdUniqueMetrics: routeDemo(Ads.getAdUniqueMetrics, LensDemoAPI.Ads.getAdUniqueMetrics, (lensId) => lensId)
  },
  LensMetrics,
  TimeSeries: {
    ...TimeSeries,
    getGroupTimeSeriesData: routeDemo(TimeSeries.getGroupTimeSeriesData, LensDemoAPI.TimeSeries.getGroupTimeSeriesData, (lensId) => lensId)
  },
  LensFolder,
  Tags: {
    ...Tags,
    getAll: routeDemo(Tags.getAll, LensDemoAPI.Tags.getAll, (lensId) => lensId),
    getTagsByCreativeHash: routeDemo(Tags.getTagsByCreativeHash, LensDemoAPI.Tags.getTagsByCreativeHash, (lensId) => lensId),
    create: routeDemo(Tags.create, LensDemoAPI.Tags.create, (tag) => tag?.lensId),
    tagCreative: routeDemo(Tags.tagCreative, LensDemoAPI.Tags.tagCreative, firstLensId),
    unTagCreative: routeDemo(Tags.unTagCreative, LensDemoAPI.Tags.unTagCreative, firstLensId)
  }
}
